import { orgContactLine } from "./orgProfile";

/**
 * Builds and opens the branded documents the system prints and exports.
 *
 * Every printed page carries the same letterhead — organisation badge, name,
 * address/contact line and TIN — followed by the document title, the reporting
 * period and who generated it, so a printout is self-identifying once it
 * leaves the screen.
 */

export const escapeHtml = (v) => {
  if (v === null || v === undefined) return "";
  return String(v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

const BRAND = "#1e3a5f";
const ACCENT = "#4f46e5";

/**
 * The organisation's badge. Only ever the organisation's own logo — a report
 * belongs to the school or company that issued it, so the Accountify crest
 * must not stand in for it. Without an uploaded badge we show the
 * organisation's initials instead.
 */
export const badgeHtml = (org, cls = "doc-badge") =>
  org.logo
    ? `<img class="${cls}" src="${escapeHtml(org.logo)}" alt="${escapeHtml(org.name)}" />`
    : `<div class="${cls} ${cls}-initials">${escapeHtml(org.initials || "")}</div>`;

/**
 * Letterhead: organisation identity across the top, then the document title in
 * its own centred band beneath the rule — the conventional report layout, and
 * it leaves the title room to breathe instead of squeezing it into a corner.
 */
export const letterheadHtml = (org, { title, subtitle, meta = [], reference } = {}) => {
  const contact = orgContactLine(org);
  const metaLine = meta.filter(Boolean).map((m) => escapeHtml(m)).join("  •  ");
  return `
    <header class="doc-head">
      <div class="doc-brand">
        ${badgeHtml(org)}
        <div class="doc-org">
          <h1>${escapeHtml(org.name)}</h1>
          ${contact ? `<div class="doc-contact">${escapeHtml(contact)}</div>` : ""}
          ${org.tin ? `<div class="doc-contact">TIN: ${escapeHtml(org.tin)}</div>` : ""}
        </div>
      </div>
    </header>
    <div class="doc-title-block">
      <div class="doc-title">${escapeHtml(title || "Report")}</div>
      ${subtitle ? `<div class="doc-subtitle">${escapeHtml(subtitle)}</div>` : ""}
      ${reference ? `<div class="doc-ref">${escapeHtml(reference)}</div>` : ""}
    </div>
    ${metaLine ? `<div class="doc-meta">${metaLine}</div>` : ""}
  `;
};

/**
 * A faint organisation mark behind the content. `position: fixed` is what makes
 * it repeat on every printed page — the browser paints fixed elements once per
 * sheet — and the low opacity keeps the figures underneath readable.
 */
export const watermarkHtml = (org) =>
  org.logo
    ? `<div class="doc-watermark" aria-hidden="true"><img src="${escapeHtml(org.logo)}" alt="" /></div>`
    : `<div class="doc-watermark doc-watermark-text" aria-hidden="true"><span>${escapeHtml(org.initials || "")}</span></div>`;

export const DOC_CSS = `
  /* A printed document is always on white paper. Pinning the colour scheme
     stops a dark-mode browser from repainting the preview window — without
     it, dark text lands on a dark background and the report is unreadable. */
  :root { color-scheme: light; }
  *, *::before, *::after { box-sizing: border-box; }
  html, body { background: #ffffff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { font-family: "Segoe UI", Arial, Helvetica, sans-serif; font-size: 12px; color: #1e293b; margin: 0; padding: 22px 26px 40px; }
  .doc-head { display: flex; align-items: center; gap: 24px;
              border-bottom: 2.5px solid ${BRAND}; padding-bottom: 12px; }
  .doc-brand { display: flex; align-items: center; gap: 14px; min-width: 0; }
  .doc-badge { height: 58px; width: auto; max-width: 190px; object-fit: contain; }
  .doc-badge-initials { height: 58px; width: 58px; border-radius: 10px; flex-shrink: 0;
                        background: ${BRAND}; color: #fff; font-size: 20px; font-weight: 700;
                        letter-spacing: .04em; display: flex; align-items: center; justify-content: center; }
  .doc-org h1 { font-size: 17px; margin: 0 0 3px; color: ${BRAND}; letter-spacing: .2px; }
  .doc-contact { font-size: 10.5px; color: #64748b; line-height: 1.45; }
  .doc-title-block { text-align: center; margin: 14px 0 4px; }
  .doc-title { font-size: 15px; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; color: ${ACCENT}; }
  .doc-subtitle { font-size: 11px; color: #475569; margin-top: 3px; }
  .doc-ref { font-size: 11px; color: #0f172a; margin-top: 3px; font-weight: 600; }
  .doc-meta { font-size: 10.5px; color: #64748b; margin: 8px 0 14px; text-align: center; }

  /* Faint organisation mark behind the content, repeated on every sheet. */
  .doc-watermark { position: fixed; inset: 0; z-index: 0; pointer-events: none;
                   display: flex; align-items: center; justify-content: center; }
  .doc-watermark img { width: 58%; max-width: 460px; height: auto; opacity: 0.06;
                       transform: rotate(-24deg); }
  .doc-watermark-text span { display: block; font-size: 150px; font-weight: 800;
                             letter-spacing: .06em; color: ${BRAND}; opacity: 0.06;
                             transform: rotate(-24deg); }
  /* Content sits above the mark. Cells are translucent rather than solid white,
     otherwise the table would blank out the mark wherever data appears. */
  .doc-body { position: relative; z-index: 1; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
  thead { display: table-header-group; }
  tr { page-break-inside: avoid; }
  th { background: ${BRAND}; color: #fff; font-size: 10.5px; font-weight: 600; text-align: left;
       padding: 7px 9px; border: 1px solid ${BRAND}; }
  td { padding: 6px 9px; border: 1px solid #e2e8f0; font-size: 10.5px; vertical-align: top;
       background: transparent; color: #1e293b; }
  tbody tr:nth-child(even) td { background: rgba(241, 245, 249, 0.7); }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
  tfoot td { font-weight: 700; background: #eef2ff; border-top: 2px solid ${ACCENT}; }
  .doc-foot { border-top: 1px solid #e2e8f0; margin-top: 18px; padding-top: 8px;
              font-size: 9.5px; color: #94a3b8; display: flex; justify-content: space-between; gap: 16px; }
  .doc-note { font-size: 10.5px; color: #475569; margin-top: 10px; font-style: italic; }
  .no-print { margin: 0 0 16px; }
  @page { size: A4; margin: 12mm; }
  @media print { .no-print { display: none !important; } body { padding: 0; } }
`;

/**
 * Renders rows into a table.  `columns` is the DataTable/ExportBar shape:
 *   [{ key, label, numeric?, render? }]
 * `render` is ignored here — printing uses raw values so the output stays text.
 */
export const tableHtml = (columns, data, { totals } = {}) => {
  if (!columns.length) return "";
  const head = columns
    .map((c) => `<th class="${c.numeric ? "num" : ""}">${escapeHtml(c.label)}</th>`)
    .join("");
  const body = data
    .map(
      (row) =>
        `<tr>${columns
          .map((c) => {
            const v = row[c.key];
            return `<td class="${c.numeric ? "num" : ""}">${
              v === null || v === undefined || v === "" ? "—" : escapeHtml(v)
            }</td>`;
          })
          .join("")}</tr>`
    )
    .join("");
  const foot = totals
    ? `<tfoot><tr>${columns
        .map((c) => `<td class="${c.numeric ? "num" : ""}">${escapeHtml(totals[c.key] ?? "")}</td>`)
        .join("")}</tr></tfoot>`
    : "";
  return `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody>${foot}</table>`;
};

/**
 * Assemble a complete branded document.
 *   org      — from useOrgProfile()
 *   title    — document title, e.g. "Trial Balance"
 *   subtitle — e.g. the period or filter description
 *   meta     — extra header facts, e.g. ["Financial Year: 2025/26", "142 records"]
 *   body     — inner HTML (usually tableHtml(...))
 *   note     — closing note, defaults to the configured invoice footer
 */
export const buildDocument = ({
  org,
  title,
  subtitle,
  reference,
  meta = [],
  body = "",
  note,
  generatedBy,
  landscape = false,
}) => {
  const stamp = new Date().toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
  const closing = note !== undefined ? note : org.footerNote;
  return `<!DOCTYPE html>
<html><head><meta charset="utf-8" />
<title>${escapeHtml(`${title || "Report"} — ${org.name}`)}</title>
<style>${DOC_CSS}${landscape ? "@page{size:A4 landscape;margin:10mm}" : ""}</style>
</head><body>
  <div class="no-print">
    <button onclick="window.print()" style="padding:7px 20px;background:${ACCENT};color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px">
      Print / Save as PDF
    </button>
  </div>
  ${watermarkHtml(org)}
  <div class="doc-body">
  ${letterheadHtml(org, { title, subtitle, meta, reference })}
  ${body}
  ${closing ? `<div class="doc-note">${escapeHtml(closing)}</div>` : ""}
  <div class="doc-foot">
    <span>${escapeHtml(org.name)}${org.tin ? ` — TIN ${escapeHtml(org.tin)}` : ""}</span>
    <span>Generated ${escapeHtml(stamp)}${generatedBy ? ` by ${escapeHtml(generatedBy)}` : ""} — Accountify</span>
  </div>
  </div>
</body></html>`;
};

/**
 * Open a document in a print window. Waits for the badge image so the
 * letterhead is never missing from the first print.
 */
export const openPrintWindow = (html, { autoPrint = false } = {}) => {
  const win = window.open("", "_blank", "width=1100,height=800");
  if (!win) {
    // eslint-disable-next-line no-alert
    alert("Please allow pop-ups for this site to print or export.");
    return null;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  if (autoPrint) {
    const fire = () => {
      win.focus();
      win.print();
    };
    const img = win.document.querySelector(".doc-badge");
    if (img && !img.complete) {
      img.addEventListener("load", fire, { once: true });
      img.addEventListener("error", fire, { once: true });
    } else {
      win.setTimeout(fire, 120);
    }
  }
  return win;
};

/** Convenience: build + open in one call. */
export const printDocument = (opts, windowOpts) =>
  openPrintWindow(buildDocument(opts), windowOpts);
